import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  ISuspiciousActivityState,
  SuspiciousActivityAlertLevel,
} from 'projects/lib-ui/src/shared/interfaces/poll-responses';

export interface IDeviceBlockState {
  alertLevel: SuspiciousActivityAlertLevel;
  expiresAt: number;
  remainingSeconds: number;
  source: 'poll-submission' | 'bootstrap' | 'resend-code';
}

@Injectable({
  providedIn: 'root',
})
export class DeviceBlockStateService {
  private readonly blockStateSubject = new BehaviorSubject<IDeviceBlockState | null>(
    null
  );
  private lastPollSubmissionPayload: {
    alertLevel: SuspiciousActivityAlertLevel;
    remainingSeconds: number;
  } | null = null;

  readonly blockState$: Observable<IDeviceBlockState | null> =
    this.blockStateSubject.asObservable();

  getBlockState(): IDeviceBlockState | null {
    return this.blockStateSubject.value;
  }

  setBlockState(
    suspiciousActivity: ISuspiciousActivityState,
    source: IDeviceBlockState['source']
  ): IDeviceBlockState {
    const now = Date.now();
    const safeRemainingSeconds = Math.max(0, suspiciousActivity.remainingSeconds || 0);
    const incomingState: IDeviceBlockState = {
      alertLevel: suspiciousActivity.alertLevel,
      remainingSeconds: safeRemainingSeconds,
      expiresAt: now + safeRemainingSeconds * 1000,
      source,
    };

    const currentState = this.blockStateSubject.value;

    if (
      currentState &&
      currentState.alertLevel === incomingState.alertLevel &&
      incomingState.alertLevel !== null
    ) {
      const duplicatePollSubmissionReemit = this.isDuplicatePollSubmissionReemit(
        currentState,
        incomingState.alertLevel,
        safeRemainingSeconds,
        source
      );
      const nextExpiresAt = duplicatePollSubmissionReemit
        ? currentState.expiresAt
        : incomingState.expiresAt;
      const reconciledState: IDeviceBlockState = {
        ...incomingState,
        expiresAt: nextExpiresAt,
        remainingSeconds: this.getRemainingSeconds(nextExpiresAt),
      };
      this.rememberPollSubmissionPayload(incomingState.alertLevel, safeRemainingSeconds, source);
      this.blockStateSubject.next(reconciledState);
      return reconciledState;
    }

    this.rememberPollSubmissionPayload(incomingState.alertLevel, safeRemainingSeconds, source);
    this.blockStateSubject.next(incomingState);
    return incomingState;
  }

  clearBlockState(): void {
    this.blockStateSubject.next(null);
    this.lastPollSubmissionPayload = null;
  }

  private isDuplicatePollSubmissionReemit(
    currentState: IDeviceBlockState,
    alertLevel: SuspiciousActivityAlertLevel,
    remainingSeconds: number,
    source: IDeviceBlockState['source']
  ): boolean {
    return (
      source === 'poll-submission' &&
      currentState.source === 'poll-submission' &&
      this.lastPollSubmissionPayload?.alertLevel === alertLevel &&
      this.lastPollSubmissionPayload?.remainingSeconds === remainingSeconds
    );
  }

  private rememberPollSubmissionPayload(
    alertLevel: SuspiciousActivityAlertLevel,
    remainingSeconds: number,
    source: IDeviceBlockState['source']
  ): void {
    if (source !== 'poll-submission' || alertLevel === null) {
      return;
    }

    this.lastPollSubmissionPayload = {
      alertLevel,
      remainingSeconds,
    };
  }

  getRemainingSeconds(expiresAt: number): number {
    return Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
  }
}
